/**
 * my-hiisi-rename.js — T-118 inline-edit for bike-card titles on /pages/my-hiisi.
 *
 * UX: customer clicks (or focuses + Enter) on the .bmh-bike-title h3 →
 * swaps to an <input> seeded with the current name → Enter or blur saves
 * → POST /apps/hiisi-bespoke/customer/rename-bike → the h3 re-renders with
 * the new name in place. Escape cancels without saving.
 *
 * Auth: App Proxy chain. The h3 carries order_id + line_item_id via
 * data attributes Liquid stamps; the customer's Shopify session is sent
 * automatically with the request (credentials: same-origin).
 *
 * Persistence boundary: the customer's typed value is the only state we
 * keep. No optimistic localStorage, no in-flight queue — a failed POST
 * reverts the h3 text and surfaces an inline error pill that auto-clears
 * after 3s. The user can retry by clicking again.
 *
 * Idempotent: clicking the same h3 multiple times doesn't stack listeners.
 * Each h3 carries `data-bmh-rename-inited="1"` after its first attachment;
 * re-renders (e.g. T-110 hydrate) re-bind only the new nodes.
 *
 * Cost-leak: this script only reads bike-card data attributes (order id,
 * line item id, current title) — no cost / sourcing / margin tokens touch
 * the rename flow. The Worker's response payload is `{ ok, name, request_id }`
 * which the static source-grep tripwire approves.
 */

(function () {
  'use strict';

  var APP_PROXY_BASE = '/apps/hiisi-bespoke';
  var NAME_MAX_LEN = 50;

  function bindOne(h3) {
    if (h3.dataset.bmhRenameInited === '1') return;
    h3.dataset.bmhRenameInited = '1';

    // Keyboard affordance — Enter triggers click since the h3 has
    // role="button". Without this, screen-reader users can focus but not
    // activate (Enter on a non-button is a no-op).
    h3.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openEditor(h3);
      }
    });
    h3.addEventListener('click', function () {
      openEditor(h3);
    });
  }

  function openEditor(h3) {
    if (h3.dataset.bmhRenameEditing === '1') return;
    h3.dataset.bmhRenameEditing = '1';

    var currentText = h3.textContent != null ? String(h3.textContent).trim() : '';
    var defaultText = h3.getAttribute('data-bmh-rename-default') || '';

    // Stash the original so cancel/error can restore exactly what was rendered
    // — including the "Bike N" / "Frameset N" default, which doesn't live in
    // the metafield.
    var originalText = currentText;

    // Seed value: if the title is currently the default placeholder ("Bike N"),
    // start the input blank so the customer doesn't have to clear the
    // placeholder before typing. Otherwise seed with the current name so a
    // minor edit (one letter) is one keystroke away.
    var seed = currentText === defaultText ? '' : currentText;

    var input = document.createElement('input');
    input.type = 'text';
    input.value = seed;
    input.maxLength = NAME_MAX_LEN;
    input.setAttribute('aria-label', 'Bike name');
    input.className = 'bmh-bike-title bmh-bike-title--editing';
    // Inline styles match the existing title rendering as closely as the
    // CSS variables let us — fonts inherit, transparent background lines
    // up with the dark card backdrop, the bottom border substitutes for
    // the click-affordance underline the h3 dropped on edit.
    input.style.font = 'inherit';
    input.style.color = 'inherit';
    input.style.background = 'transparent';
    input.style.border = 'none';
    input.style.borderBottom = '1px solid rgba(232, 230, 225, 0.5)';
    input.style.padding = '0 2px';
    input.style.maxWidth = '100%';
    input.style.width = '100%';
    input.style.outline = 'none';

    // Swap. Hide the h3 with hidden=true rather than removing it — that way
    // the saveAndClose path can restore the original text without rebuilding
    // the h3 from its attributes (which would lose any sibling event hooks
    // the page wired up).
    h3.hidden = true;
    h3.parentNode.insertBefore(input, h3.nextSibling);

    var done = false;

    function restore(textToShow) {
      if (done) return;
      done = true;
      h3.textContent = textToShow;
      h3.hidden = false;
      h3.dataset.bmhRenameEditing = '';
      if (input.parentNode) input.parentNode.removeChild(input);
    }

    function flashError(reason) {
      // The h3 itself is the cheapest error surface — flip aria-invalid +
      // append a brief sibling pill that auto-clears. Keeps the rename
      // affordance discoverable without growing the page DOM permanently.
      h3.setAttribute('aria-invalid', 'true');
      var pill = document.createElement('span');
      pill.className = 'bmh-bike-title__error';
      pill.setAttribute('role', 'alert');
      pill.textContent = reason;
      pill.style.display = 'inline-block';
      pill.style.marginLeft = '8px';
      pill.style.padding = '2px 8px';
      pill.style.fontSize = '12px';
      pill.style.background = 'rgba(232, 109, 109, 0.18)';
      pill.style.color = '#9a2622';
      pill.style.borderRadius = '999px';
      h3.parentNode.insertBefore(pill, h3.nextSibling);
      setTimeout(function () {
        if (pill.parentNode) pill.parentNode.removeChild(pill);
        h3.removeAttribute('aria-invalid');
      }, 3000);
    }

    function save() {
      var typed = input.value != null ? String(input.value).trim() : '';
      if (typed.length === 0) {
        restore(originalText);
        return;
      }
      if (typed.length > NAME_MAX_LEN) {
        // The maxlength attribute should prevent this in practice; defensive.
        flashError('Name too long');
        restore(originalText);
        return;
      }
      if (typed === originalText) {
        // No-op edit — don't burn a Worker round-trip.
        restore(originalText);
        return;
      }

      // Optimistic: show the new name immediately. On failure we restore.
      restore(typed);

      var orderId = h3.getAttribute('data-bmh-rename-order-id') || '';
      var lineItemId = h3.getAttribute('data-bmh-rename-lid') || '';
      if (!orderId || !lineItemId) {
        flashError('Missing context — reload the page');
        h3.textContent = originalText;
        return;
      }

      fetch(APP_PROXY_BASE + '/customer/rename-bike', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          line_item_id: lineItemId,
          name: typed,
        }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (payload) {
              var err = new Error(
                (payload && (payload.error || payload.safeMessage)) || 'HTTP ' + res.status,
              );
              throw err;
            });
          }
          return res.json().catch(function () { return {}; });
        })
        .then(function (payload) {
          // The Worker echoes the trimmed name back; use that so any
          // server-side normalization (currently just trim, but defensive
          // for future tightening) is what stays on screen.
          if (payload && typeof payload.name === 'string' && payload.name.length > 0) {
            h3.textContent = payload.name;
          }
          // Update the aria-label so the next focus reads the new value.
          h3.setAttribute('aria-label', 'Rename ' + (h3.textContent || ''));
        })
        .catch(function (err) {
          // Roll back the optimistic update.
          h3.textContent = originalText;
          flashError(
            err && err.message
              ? 'Save failed: ' + err.message
              : 'Save failed',
          );
        });
    }

    function cancel() {
      restore(originalText);
    }

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    });
    input.addEventListener('blur', save);

    // Wait a tick so the click that opened the editor doesn't immediately
    // blur the new input on Safari.
    setTimeout(function () {
      try { input.focus(); input.select(); } catch (_) { /* ignore */ }
    }, 0);
  }

  function bindAll(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-bmh-rename]');
    for (var i = 0; i < nodes.length; i++) {
      bindOne(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindAll(); });
  } else {
    bindAll();
  }

  // T-110 hydrate re-renders bike-cards in place. Re-bind any new h3 that
  // appears after our initial pass. MutationObserver is cheaper than
  // polling; data-bmh-rename-inited makes bindOne a no-op for already-bound
  // nodes so the observer can fire freely.
  if (typeof MutationObserver === 'function') {
    var observer = new MutationObserver(function () { bindAll(); });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Expose for tests / SPA-style remounts. Not a public contract.
  window.__hiisiRenameBindAll = bindAll;
})();
